/* ============================================================
   Sahab Systems — branded QR per QR Code Spec.md
   - Real ISO/IEC 18004 QR (Byte/UTF-8), ECC Level H
   - 4-module quiet zone
   - Ink modules on field-colored background (themed via CSS vars)
   - Centered MCP-core mark on a field-colored plate, ≤22% of width
   ============================================================ */
import { QRCodeSVG } from "qrcode.react";
import { forwardRef, useMemo, type CSSProperties } from "react";

type Props = {
  /** URL to encode — usually `${API_BASE}/c/{short_code}` so /c/:code can log + redirect. */
  url: string;
  /** Pixel size of the rendered QR square (defaults to 132). */
  size?: number;
  /** Ink color for data modules. Defaults to ink. */
  fgColor?: string;
  /** Background color (also the logo plate). Defaults to field. */
  bgColor?: string;
  /** Cyan accent on the mark's pulse-node + center. Defaults to signal. */
  signalColor?: string;
  className?: string;
  style?: CSSProperties;
};

/**
 * Inline MCP-core mark with colors baked in (no currentColor, no CSS vars) so
 * it can be embedded as a data URI inside QRCodeSVG's imageSettings.
 */
function buildMarkSvg(fg: string, sig: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">
    <circle cx="60" cy="60" r="26" fill="none" stroke="${fg}" stroke-width="1.6" opacity=".9"/>
    <line x1="60" y1="46" x2="60" y2="22" stroke="${sig}" stroke-width="2.2" stroke-linecap="round"/>
    <rect x="56.6" y="18.6" width="6.8" height="6.8" rx=".8" fill="${sig}"/>
    <line x1="69.9" y1="50.1" x2="86.87" y2="33.13" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" opacity=".9"/>
    <circle cx="86.87" cy="33.13" r="3.2" fill="${fg}"/>
    <line x1="74" y1="60" x2="98" y2="60" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" opacity=".9"/>
    <rect x="94.8" y="56.8" width="6.4" height="6.4" rx=".8" fill="${fg}"/>
    <line x1="69.9" y1="69.9" x2="86.87" y2="86.87" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" opacity=".9"/>
    <circle cx="86.87" cy="86.87" r="3.2" fill="${fg}"/>
    <line x1="60" y1="74" x2="60" y2="98" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" opacity=".9"/>
    <rect x="56.8" y="94.8" width="6.4" height="6.4" rx=".8" fill="${fg}"/>
    <line x1="50.1" y1="69.9" x2="33.13" y2="86.87" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" opacity=".9"/>
    <circle cx="33.13" cy="86.87" r="3.2" fill="${fg}"/>
    <line x1="46" y1="60" x2="22" y2="60" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" opacity=".9"/>
    <rect x="18.8" y="56.8" width="6.4" height="6.4" rx=".8" fill="${fg}"/>
    <line x1="50.1" y1="50.1" x2="33.13" y2="33.13" stroke="${fg}" stroke-width="1.6" stroke-linecap="round" opacity=".9"/>
    <circle cx="33.13" cy="33.13" r="3.2" fill="${fg}"/>
    <circle cx="60" cy="60" r="11" fill="${fg}"/>
    <circle cx="60" cy="60" r="6.5" fill="none" stroke="${sig}" stroke-width="1.8"/>
    <circle cx="60" cy="60" r="2.4" fill="${sig}"/>
  </svg>`;
}

function markDataUri(fg: string, sig: string): string {
  return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(buildMarkSvg(fg, sig))))}`;
}

/**
 * Real QR code per QR Code Spec.md (Byte/UTF-8, ECC H, 4-module margin) with
 * the MCP-core mark centered as a data-URI overlay. qrcode.react handles
 * `excavate: true` so modules under the mark are cleared for legibility.
 *
 * forwardRef exposes the rendered <svg> so consumers can serialize it for
 * SVG / PNG export (admin uses this for printable QR downloads).
 */
export const SahabQR = forwardRef<SVGSVGElement, Props>(function SahabQR(
  {
    url,
    size = 132,
    fgColor = "#0B0F1C",
    bgColor = "#F2EFE8",
    signalColor = "#7EC9E0",
    className,
    style,
  },
  ref,
) {
  // ≤22% per spec §5; size in pixels of the mark image inside the QR.
  const markPx = Math.round(size * 0.22);
  const dataUri = useMemo(
    () => markDataUri(fgColor, signalColor),
    [fgColor, signalColor],
  );
  return (
    <QRCodeSVG
      ref={ref}
      value={url}
      size={size}
      bgColor={bgColor}
      fgColor={fgColor}
      level="H"
      includeMargin
      className={className}
      style={{ display: "block", ...style }}
      imageSettings={{
        src: dataUri,
        height: markPx,
        width: markPx,
        excavate: true,
      }}
    />
  );
});

/**
 * SVG → PNG blob at the spec's 1024px floor. Used by admin for printable QRs.
 */
export async function qrSvgToPngBlob(
  svg: SVGSVGElement,
  pxSize = 1024,
  bgColor = "#F2EFE8",
): Promise<Blob> {
  const xml = new XMLSerializer().serializeToString(svg);
  const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(xml)))}`;
  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error("QR SVG failed to load into image"));
    img.src = dataUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = pxSize;
  canvas.height = pxSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  ctx.imageSmoothingEnabled = false;
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, pxSize, pxSize);
  ctx.drawImage(img, 0, 0, pxSize, pxSize);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob returned null"))),
      "image/png",
    );
  });
}

export function qrSvgToSvgBlob(svg: SVGSVGElement): Blob {
  const xml = new XMLSerializer().serializeToString(svg);
  return new Blob([xml], { type: "image/svg+xml" });
}
